"use client";

import { useTranslations } from "use-intl";

import { ArrowBackIcon } from "@/components/appIcons";
import { usePhoneOtpAuth, type PhoneOtpAuthOptions } from "@/hooks/use-phone-otp-auth";

import { WhatsAppIcon } from "./icons/whatsapp-icon";
import { PhoneOtpForm } from "./phone-otp-form";
import { Button } from "./ui/button";

interface PhoneOtpAuthProps extends PhoneOtpAuthOptions {
  autoFocusPhone?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
}

export function PhoneOtpAuth(props: PhoneOtpAuthProps) {
  const t = useTranslations("globals.auth");
  const auth = usePhoneOtpAuth(props);

  function expand() {
    auth.expand();
    props.onExpandedChange?.(true);
  }

  function collapse() {
    auth.collapse();
    props.onExpandedChange?.(false);
  }

  if (!auth.available) return null;
  if (!auth.expanded) {
    return (
      <Button
        type="button"
        variant="outline"
        className="h-12 w-full gap-2 text-base"
        size="lg"
        onClick={expand}
      >
        <WhatsAppIcon className="size-5 text-[#25D366]" />
        {t("continueWithWhatsApp", { mode: props.mode })}
      </Button>
    );
  }

  return (
    <div className="space-y-3">
      {props.onExpandedChange && (
        <Button type="button" variant="ghost" size="sm" className="gap-1" onClick={collapse}>
          <ArrowBackIcon className="size-4" decorative />
          {t("back")}
        </Button>
      )}

      <div className="space-y-4 rounded-xl border border-[#25D366]/25 bg-[#25D366]/5 p-4">
        <p className="text-sm leading-6 text-muted-foreground">
          {t("whatsappAuthDescription", { mode: props.mode })}
        </p>
        <PhoneOtpForm
          mode={props.mode}
          country={auth.phoneCountry}
          phone={auth.phone}
          autoFocusPhone={props.autoFocusPhone}
          normalizedPhone={auth.normalizedPhone}
          code={auth.code}
          sending={auth.sending}
          verifying={auth.verifying}
          resendSeconds={auth.resendSeconds}
          onCountryChange={auth.setPhoneCountry}
          onPhoneChange={auth.setPhone}
          onCodeChange={auth.setCode}
          onSendCode={auth.sendCode}
          onVerifyCode={auth.verifyCode}
        />
      </div>
    </div>
  );
}
